<template>
  <div class="space-y-6">
    <!-- Header -->
    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
      <div>
        <h1 class="text-2xl font-semibold mb-0 text-gray-900">Moje ogłoszenia</h1>
      </div>
      <div class="flex gap-3">
        <button
          @click="$emit('create')"
          class="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors font-medium"
        >
          <font-awesome-icon icon="plus" class="w-4 h-4" />
          Dodaj ogłoszenie
        </button>
      </div>
    </div>

    <!-- Filters -->
    <div class="bg-white rounded-lg border border-slate-200">
      <div class="p-4">
        <div class="flex flex-col sm:flex-row gap-4">
          <div class="flex-1">
            <div class="relative">
              <font-awesome-icon icon="search" class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                v-model="searchTerm"
                type="text"
                placeholder="Szukaj ogłoszeń..."
                class="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <select
            v-model="statusFilter"
            class="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full sm:w-48"
          >
            <option value="all">Wszystkie statusy</option>
            <option value="1">Aktywne</option>
            <option value="0">Oczekujące</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Stats -->
    <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div class="bg-white rounded-lg border border-slate-200">
        <div class="p-4">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600">Wszystkie</p>
              <p class="text-2xl font-bold text-gray-900">{{ offers.length }}</p>
            </div>
            <div class="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
              <font-awesome-icon icon="tag" class="w-4 h-4 text-blue-600" />
            </div>
          </div>
        </div>
      </div>
      <div class="bg-white rounded-lg border border-slate-200">
        <div class="p-4">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600">Aktywne</p>
              <p class="text-2xl font-bold text-green-600">{{ activeOffers }}</p>
            </div>
            <div class="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
              <font-awesome-icon icon="check-circle" class="w-4 h-4 text-green-600" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-8">
      <div class="inline-block w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      <p class="text-gray-600 mt-2">Ładowanie ogłoszeń...</p>
    </div>

    <!-- Empty State -->
    <div v-else-if="filteredOffers.length === 0 && offers.length === 0" class="bg-white rounded-lg border border-slate-200">
      <div class="p-8 text-center">
        <div class="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mx-auto mb-4">
          <font-awesome-icon icon="tag" class="w-6 h-6 text-gray-400" />
        </div>
        <h3 class="text-lg font-medium text-gray-900 mb-2">Brak ogłoszeń</h3>
        <p class="text-gray-600 mb-4">Nie masz jeszcze żadnych ogłoszeń. Dodaj pierwsze ogłoszenie.</p>
        <button
          @click="$emit('create')"
          class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
        >
          Dodaj ogłoszenie
        </button>
      </div>
    </div>

    <!-- No Results -->
    <div v-else-if="filteredOffers.length === 0" class="bg-white rounded-lg border border-slate-200">
      <div class="p-8 text-center">
        <div class="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mx-auto mb-4">
          <font-awesome-icon icon="search" class="w-6 h-6 text-gray-400" />
        </div>
        <h3 class="text-lg font-medium text-gray-900 mb-2">Brak wyników</h3>
        <p class="text-gray-600 mb-4">Nie znaleziono ogłoszeń spełniających kryteria wyszukiwania.</p>
        <button
          @click="clearFilters"
          class="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
        >
          Wyczyść filtry
        </button>
      </div>
    </div>

    <!-- Listings -->
    <div v-else class="space-y-4">
      <div 
        v-for="offer in filteredOffers" 
        :key="offer.id"
        class="offer-card bg-white rounded-lg border border-slate-200 hover:shadow-md transition-shadow"
      >
        <div class="p-4">
          <div class="flex flex-col lg:flex-row lg:items-center gap-3">
            <!-- Main Info -->
            <div class="flex-1 min-w-0">
              <div class="mb-2">
                <div class="flex items-start justify-between mb-1">
                  <h3 class="offer-title text-lg font-medium text-gray-900 flex-1 mr-2 leading-tight">{{ offer.name }}</h3>
                  <template v-if="offer.archived === 1">
                    <div class="flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-md whitespace-nowrap">
                      <font-awesome-icon icon="archive" class="w-3 h-3" />
                      Archiwalne
                    </div>
                  </template>
                  <template v-else>
                    <div v-if="offer.active === 1" class="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-md whitespace-nowrap">
                      <font-awesome-icon icon="check-circle" class="w-3 h-3" />
                      Aktywne
                    </div>
                    <div v-else class="flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-md whitespace-nowrap">
                      <font-awesome-icon icon="clock" class="w-3 h-3" />
                      Oczekujące
                    </div>
                  </template>
                </div>
              </div>

              <p v-if="offer.description" class="offer-description text-sm text-gray-600 mb-2">{{ offer.description }}</p>

              <div class="flex flex-wrap items-center gap-3 text-sm text-gray-500">
                <div v-if="offer.category_name" class="flex items-center gap-1">
                  <font-awesome-icon icon="tag" class="w-4 h-4" />
                  {{ offer.category_name }}
                </div>
                <div v-if="offer.product_name" class="flex items-center gap-1">
                  <font-awesome-icon icon="link" class="w-4 h-4" />
                  {{ offer.product_name }}
                </div>
                <div v-if="offer.images?.length" class="flex items-center gap-1">
                  <font-awesome-icon icon="image" class="w-4 h-4" />
                  {{ offer.images.length }} {{ offer.images.length === 1 ? 'zdjęcie' : 'zdjęć' }}
                </div>
              </div>
            </div>

            <!-- Price & Actions -->
            <div class="flex items-center gap-3">
              <!-- Price -->
              <div class="text-right">
                <div class="text-lg font-semibold text-gray-900">
                  {{ parseFloat(offer.price || '0').toFixed(2) }} PLN
                </div>
                <p class="text-xs text-gray-500">Cena</p>
              </div>

              <!-- Actions Dropdown -->
              <div class="relative" v-if="offer.archived !== 1">
                <button
                  @click="toggleDropdown(offer.id)"
                  class="flex items-center justify-center w-8 h-8 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all bg-transparent border-0 p-0"
                >
                  <font-awesome-icon icon="ellipsis-h" class="w-4 h-4" />
                </button>
                
                <div 
                  v-if="openDropdown === offer.id"
                  class="absolute right-0 mt-2 w-40 bg-white rounded-sm shadow-lg border border-gray-100 z-10 py-1"
                >
                  <button
                    @click="handleEdit(offer)"
                    class="dropdown-btn dropdown-btn-edit w-full px-4 py-2 text-left text-sm text-gray-700 flex items-center gap-2"
                  >
                    <font-awesome-icon icon="edit" class="w-4 h-4" />
                    Edytuj
                  </button>
                  <button
                    @click="confirmDelete(offer)"
                    class="dropdown-btn dropdown-btn-delete w-full px-4 py-2 text-left text-sm text-red-600 flex items-center gap-2"
                  >
                    <font-awesome-icon icon="trash" class="w-4 h-4" />
                    Usuń
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { ref, computed } from 'vue';
import type { OfferDto } from '../types/offer.types';

const props = defineProps<{ offers: OfferDto[]; loading: boolean }>();
const emit = defineEmits<{
  edit: [offer: OfferDto];
  create: [];
  delete: [offer: OfferDto];
}>();

const searchTerm = ref('');
const statusFilter = ref('all');
const openDropdown = ref<number | null>(null);

// Computed properties
const filteredOffers = computed(() => {
  return props.offers.filter(offer => {
    const matchesSearch = 
      offer.name.toLowerCase().includes(searchTerm.value.toLowerCase()) ||
      (offer.description?.toLowerCase().includes(searchTerm.value.toLowerCase()) ?? false);
    
    const matchesStatus = 
      statusFilter.value === 'all' || 
      String(offer.active) === statusFilter.value;
    
    return matchesSearch && matchesStatus;
  });
});

const activeOffers = computed(() => {
  return props.offers.filter(offer => offer.active === 1).length;
});

// Methods
function clearFilters() {
  searchTerm.value = '';
  statusFilter.value = 'all';
}

function toggleDropdown(offerId: number) {
  openDropdown.value = openDropdown.value === offerId ? null : offerId;
}

function handleEdit(offer: OfferDto) {
  openDropdown.value = null;
  if ((offer as any).archived === 1) {
    alert('Nie można edytować zarchiwizowanego ogłoszenia.');
    return;
  }
  emit('edit', offer);
}

function confirmDelete(offer: OfferDto) {
  openDropdown.value = null;
  if ((offer as any).archived === 1) {
    alert('Nie można usuwać zarchiwizowanego ogłoszenia.');
    return;
  }
  if (confirm(`Czy na pewno chcesz usunąć ogłoszenie "${offer.name}"?`)) {
    emit('delete', offer);
  }
}
</script>

<style>
.dropdown-btn {
  background: transparent !important;
  border: none !important;
  outline: none !important;
  transition: background-color 0.2s ease !important;
}

.dropdown-btn:focus {
  outline: none !important;
  box-shadow: none !important;
}

.dropdown-btn-edit:hover {
  background-color: #f9fafb !important;
}

.dropdown-btn-delete:hover {
  background-color: #fef2f2 !important;
}

.offer-card {
  min-height: 140px;
}

.offer-title {
  /* Fallback dla starszych przeglądarek */
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  white-space: nowrap !important;
  
  /* Nowoczesne przeglądarki */
  display: -webkit-box !important;
  -webkit-line-clamp: 2 !important;
  -webkit-box-orient: vertical !important;
  white-space: normal !important;
  
  height: 3.5rem !important;
  line-height: 1.75rem !important;
  word-break: break-word !important;
  max-height: 3.5rem !important;
}

.offer-description {
  /* Fallback */
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  white-space: nowrap !important;
  
  /* Nowoczesne przeglądarki */
  display: -webkit-box !important;
  -webkit-line-clamp: 2 !important;
  -webkit-box-orient: vertical !important;
  white-space: normal !important;
  
  height: 2.5rem !important;
  line-height: 1.25rem !important;
  word-break: break-word !important;
  max-height: 2.5rem !important;
}
</style>
